import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  TrendingUp,
  Award,
  Radio,
  Music,
  DollarSign,
  ShieldCheck,
  Mail,
  Phone,
  ArrowLeft,
  CheckCircle2,
  Building2,
  Globe,
  Sparkles,
  Wine,
  UtensilsCrossed,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  festivalInfo,
  locationsConcept,
  programHighlights,
  sampleProgram,
  worldCuisines,
  galleryImages,
  references,
  budget,
  cityBenefits,
  partnership,
  premiumOption,
  contactInfo,
  heroImage,
  videoUrl
} from '../data/luhacoviceData';

const LuhacoviceLanding = () => {
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  const INITIAL_PHOTOS_COUNT = 15;
  const displayedImages = showAllPhotos ? galleryImages : galleryImages?.slice(0, INITIAL_PHOTOS_COUNT);

  const iconComponents = {
    Award, TrendingUp, Radio, Music, DollarSign, ShieldCheck, Building2, Sparkles, Wine
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-gradient-to-r from-blue-900 to-blue-700 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-blue-100 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Zpět na přehled</span>
            </Button>
            <h2 className="text-2xl font-bold text-white">
              GASTROFESTIVAL <span className="text-yellow-400">LUHAČOVICE</span>
            </h2>
          </div>
          <Button 
            onClick={scrollToContact}
            className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold px-8 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            KONTAKTOVAT
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt="Gastrofestival"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-blue-800/85 to-purple-900/90"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="mb-4">
            <p className="text-yellow-400 text-lg tracking-widest font-semibold">PARTNERSKÁ NABÍDKA PRO MĚSTO</p>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">GASTROFESTIVAL</span><br />
            <span className="text-yellow-400">CESTA KOLEM SVĚTA</span>
          </h1>
          
          <p className="text-2xl md:text-3xl mb-8 text-blue-100 font-light">
            Luhačovice 2027
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Users className="h-10 w-10 text-yellow-400 mx-auto mb-3" />
              <div className="text-3xl font-bold mb-1">{festivalInfo.expectedVisitors}</div>
              <div className="text-blue-100">návštěvníků</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <MapPin className="h-10 w-10 text-yellow-400 mx-auto mb-3" />
              <div className="text-3xl font-bold mb-1">3 lokace</div>
              <div className="text-blue-100">v centru města</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Globe className="h-10 w-10 text-yellow-400 mx-auto mb-3" />
              <div className="text-3xl font-bold mb-1">6 kontinentů</div>
              <div className="text-blue-100">světové kuchyně</div>
            </div>
          </div>

          <Button 
            onClick={scrollToContact}
            className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold text-lg px-12 py-6 rounded-full shadow-2xl hover:shadow-yellow-500/50 transition-all transform hover:scale-105"
          >
            CHCI VÍCE INFORMACÍ
          </Button>
        </div>
      </section>

      {/* Video Section */}
      {videoUrl && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
              Podívejte se na <span className="text-blue-600">úspěšný festival</span>
            </h2>
            <p className="text-center text-gray-600 mb-8 text-lg">
              Reference z Gastrofestivalu Uherské Hradiště
            </p>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ maxHeight: '500px' }}>
              <video 
                controls 
                className="w-full h-auto"
                style={{ maxHeight: '500px', objectFit: 'contain' }}
                poster={heroImage}
              >
                <source src={videoUrl} type="video/mp4" />
                Váš prohlížeč nepodporuje video.
              </video>
            </div>
          </div>
        </section>
      )}

      {/* Koncept - 3 lokace */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              {locationsConcept.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {locationsConcept.description}
            </p>
          </div>

          {/* Interaktivní mapa s lokacemi */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center text-gray-900">
              <MapPin className="inline h-6 w-6 text-blue-600 mb-1" /> Festivalové zóny
            </h3>
            
            {/* Vizuální mapa s 3 lokacemi */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 mb-8">
              <h3 className="text-3xl font-bold text-white text-center mb-6">
                <MapPin className="inline h-8 w-8 mb-1" /> 3 Festivalové zóny v centru Luhačovic
              </h3>
              
              {/* Grafická reprezentace lokací */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {locationsConcept.locations?.map((loc, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 text-center shadow-lg hover:shadow-xl transition-shadow">
                      <div className="bg-gradient-to-br from-yellow-400 to-orange-500 w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center">
                        <span className="text-white text-2xl font-bold">{loc.id}</span>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">{loc.name}</h4>
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-600 mb-3">
                        <MapPin className="h-3 w-3" />
                        <span>{loc.gps.lat}°N</span>
                        <span>{loc.gps.lng}°E</span>
                      </div>
                      <div className="flex flex-wrap gap-1 justify-center">
                        {loc.continents?.map((continent, i) => (
                          <span key={i} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
                            {continent}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="text-center mt-6">
                  <p className="text-white text-sm">
                    📍 Všechny lokace jsou v docházkové vzdálenosti 2-5 minut
                  </p>
                </div>
              </div>
              
              {/* Informace o přístupnosti */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/10 rounded-lg p-4 text-center text-white">
                  <p className="text-3xl font-bold mb-1">🚶‍♂️</p>
                  <p className="font-semibold">Pěší trasa</p>
                  <p className="text-sm text-blue-100">Max 5 min mezi lokacemi</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4 text-center text-white">
                  <p className="text-3xl font-bold mb-1">🅿️</p>
                  <p className="font-semibold">Parkování</p>
                  <p className="text-sm text-blue-100">Dostupné v centru města</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4 text-center text-white">
                  <p className="text-3xl font-bold mb-1">♿</p>
                  <p className="font-semibold">Bezbariérový přístup</p>
                  <p className="text-sm text-blue-100">Všechny lokace přístupné</p>
                </div>
              </div>
            </div>

            {/* Karty lokací */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {locationsConcept.locations?.map((location, idx) => (
                <Card 
                  key={location.id}
                  className="hover:shadow-xl transition-shadow cursor-pointer border-2 hover:border-blue-500"
                  onClick={() => setSelectedLocation(selectedLocation === idx ? null : idx)}
                >
                  <CardHeader className="bg-gradient-to-br from-blue-600 to-purple-600 text-white">
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Lokace {location.id}
                    </CardTitle>
                    <p className="text-sm font-semibold text-blue-100">{location.name}</p>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="mb-4">
                      <p className="font-bold text-lg text-gray-900 mb-2">Kontinenty:</p>
                      <div className="flex flex-wrap gap-2">
                        {location.continents?.map((continent, i) => (
                          <span key={i} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                            {continent}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{location.description}</p>
                    
                    {selectedLocation === idx && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="font-semibold text-gray-900 mb-2">Gastronomie:</p>
                        <ul className="space-y-1">
                          {location.features?.map((feature, i) => (
                            <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                          <p className="text-sm font-semibold text-yellow-800">
                            🍳 {location.show}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Zážitek pro návštěvníky */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-gray-900">Co zážije návštěvník</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {locationsConcept.visitorExperience?.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reference z Uherského Hradiště */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              {references.title}
            </h2>
            <p className="text-xl text-gray-600">
              {references.event} • {references.edition}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="text-center bg-gradient-to-br from-blue-600 to-blue-700 text-white">
              <CardContent className="pt-6">
                <Users className="h-12 w-12 mx-auto mb-3" />
                <p className="text-4xl font-bold mb-2">{references.statistics.visitors}</p>
                <p className="text-blue-100">návštěvníků</p>
              </CardContent>
            </Card>
            
            <Card className="text-center bg-gradient-to-br from-purple-600 to-purple-700 text-white">
              <CardContent className="pt-6">
                <Calendar className="h-12 w-12 mx-auto mb-3" />
                <p className="text-4xl font-bold mb-2">{references.statistics.days}</p>
                <p className="text-purple-100">festivalových dnů</p>
              </CardContent>
            </Card>
            
            <Card className="text-center bg-gradient-to-br from-green-600 to-green-700 text-white">
              <CardContent className="pt-6">
                <Building2 className="h-12 w-12 mx-auto mb-3" />
                <p className="text-4xl font-bold mb-2">{references.statistics.vendors}</p>
                <p className="text-green-100">prodejců</p>
              </CardContent>
            </Card>
            
            <Card className="text-center bg-gradient-to-br from-yellow-600 to-yellow-700 text-white">
              <CardContent className="pt-6">
                <Award className="h-12 w-12 mx-auto mb-3" />
                <p className="text-4xl font-bold mb-2">{references.statistics.satisfaction}</p>
                <p className="text-yellow-100">spokojených návštěvníků</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-6 text-gray-900">Mediální dosah</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {references.mediaReach?.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Radio className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vzorový program */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              {sampleProgram.title}
            </h2>
            <p className="text-xl text-gray-600 mb-4">
              {sampleProgram.subtitle}
            </p>
            <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-full font-bold shadow-lg">
              {sampleProgram.note}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {sampleProgram?.categories?.map((category, idx) => {
              const IconComponent = iconComponents[category.icon];
              return (
                <Card key={idx} className="hover:shadow-2xl transition-shadow border-2 border-purple-200">
                  <CardHeader className="bg-gradient-to-br from-purple-600 to-pink-600 text-white">
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      {IconComponent && <IconComponent className="h-7 w-7" />}
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {category.performers ? (
                      <div className="space-y-4">
                        {category.performers?.map((performer, i) => (
                          <div key={i} className="pb-4 border-b border-gray-200 last:border-0">
                            <h4 className="font-bold text-gray-900 mb-2">{performer.name}</h4>
                            <p className="text-sm text-gray-600">{performer.description}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ul className="space-y-3">
                        {category.items?.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Světové kuchyně */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              <UtensilsCrossed className="inline h-12 w-12 text-orange-600 mb-2" />
              <br />
              {worldCuisines.title}
            </h2>
            <p className="text-xl text-gray-600">
              {worldCuisines.subtitle}
            </p>
          </div>

          <div className="space-y-8">
            {worldCuisines?.continents?.map((continent, idx) => (
              <Card key={idx} className="border-2 border-gray-200 hover:border-orange-400 transition-all overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-orange-100 to-yellow-100">
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <span className="text-4xl">{continent.flag}</span>
                    <span className="text-gray-900">{continent.name}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {continent.vendors?.map((vendor, vIdx) => (
                    <div key={vIdx} className="mb-6 last:mb-0">
                      <div className="mb-4">
                        <h4 className="font-bold text-lg text-gray-900">{vendor.name}</h4>
                        {vendor.location && <p className="text-sm text-gray-600">📍 {vendor.location}</p>}
                        {vendor.owner && <p className="text-sm text-gray-600">👨‍🍳 {vendor.owner}</p>}
                        {vendor.description && <p className="text-sm text-gray-600 italic mt-2">{vendor.description}</p>}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vendor.dishes?.map((dish, dIdx) => (
                          <div 
                            key={dIdx} 
                            className={`p-4 rounded-lg ${
                              dish.highlight 
                                ? 'bg-gradient-to-br from-yellow-100 to-orange-100 border-2 border-yellow-400' 
                                : dish.vegan 
                                  ? 'bg-green-50 border border-green-200' 
                                  : 'bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h5 className="font-semibold text-gray-900">{dish.name}</h5>
                              {dish.highlight && <Star className="h-5 w-5 text-yellow-600 flex-shrink-0" />}
                              {dish.vegan && <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full flex-shrink-0">VEGAN</span>}
                            </div>
                            {dish.description && (
                              <p className="text-sm text-gray-600 mb-1">{dish.description}</p>
                            )}
                            {dish.note && (
                              <p className="text-xs text-gray-500 italic">→ {dish.note}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Fun fact */}
          <div className="mt-12 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">🌍 Skutečná cesta kolem světa!</h3>
            <p className="text-xl mb-2">
              <strong>12+ zemí</strong> • <strong>40+ pokrmů</strong> • <strong>Nezapomenutelný zážitek</strong>
            </p>
            <p className="text-lg text-orange-100">
              Návštěvníci mohou ochutnat autentické speciality připravované rodilými kuchaři a food truckaři s mezinárodní zkušeností!
            </p>
          </div>
        </div>
      </section>

      {/* Fotogalerie */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              📸 Atmosféra festivalu
            </h2>
            <p className="text-xl text-gray-600">
              Podívejte se na fotky z Gastrofestivalu Uherské Hradiště
            </p>
          </div>

          {/* Grid galerie */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedImages?.map((image, idx) => (
              <div 
                key={idx}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105"
                onClick={() => setLightboxImage(image)}
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-200">
                  <img 
                    src={image.url}
                    alt={`Gastrofestival fotka ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Tlačítko Zobrazit více */}
          {galleryImages && galleryImages.length > INITIAL_PHOTOS_COUNT && (
            <div className="text-center mt-8">
              <Button
                onClick={() => setShowAllPhotos(!showAllPhotos)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold text-lg px-12 py-6 rounded-full shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
              >
                {showAllPhotos ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    Zobrazit méně
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    Zobrazit více ({galleryImages.length - INITIAL_PHOTOS_COUNT} dalších fotek)
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Call to action */}
          <div className="mt-12 text-center bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-3">✨ Vytvořme stejnou atmosféru v Luhačovicích!</h3>
            <p className="text-lg text-orange-100">
              Tisíce spokojených návštěvníků, autentické chutě ze světa, nezapomenutelné zážitky.
            </p>
          </div>
        </div>
      </section>

      {/* Lightbox pro zvětšení fotek */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-6xl w-full">
            {/* Zavírací tlačítko */}
            <button 
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              onClick={() => setLightboxImage(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Obrázek */}
            <img 
              src={lightboxImage.url}
              alt="Gastrofestival"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Benefity pro město */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {cityBenefits.title}
            </h2>
            <p className="text-xl text-blue-100">
              {cityBenefits.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cityBenefits.benefits?.map((benefit, idx) => {
              const IconComponent = iconComponents[benefit.icon];
              return (
                <Card key={idx} className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 transition-all">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      {IconComponent && <IconComponent className="h-8 w-8 text-yellow-400" />}
                      <CardTitle className="text-white text-xl">{benefit.title}</CardTitle>
                    </div>
                    <p className="text-blue-100">{benefit.description}</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {benefit.highlights?.map((highlight, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-blue-100">
                          <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Finanční nabídka */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              {partnership.title}
            </h2>
            <p className="text-xl text-gray-600">
              {partnership.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            {/* Co město dostane */}
            <Card className="border-2 border-green-500">
              <CardHeader className="bg-gradient-to-br from-green-600 to-green-700 text-white">
                <CardTitle className="text-2xl">✅ Co město získá</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {partnership.whatCityGets?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Co zajistíme */}
            <Card className="border-2 border-blue-500">
              <CardHeader className="bg-gradient-to-br from-blue-600 to-blue-700 text-white">
                <CardTitle className="text-2xl">🎯 Co zajistíme my</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3">
                  {partnership.whatWeProvide?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Finanční přehled */}
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 border-2 border-yellow-400">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Finanční podmínky</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <p className="text-sm text-gray-600 mb-2">Celkový rozpočet</p>
                  <p className="text-3xl font-bold text-gray-900">{partnership.totalBudget}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 shadow-lg text-white">
                  <p className="text-sm text-blue-100 mb-2">Příspěvek města</p>
                  <p className="text-3xl font-bold">{partnership.cityContribution}</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <p className="text-sm text-gray-600 mb-2">Podíl města</p>
                  <p className="text-3xl font-bold text-gray-900">{partnership.percentage}</p>
                </div>
              </div>
            </div>

            {/* Detailní rozpočet - Vizualizace */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
                <h4 className="text-2xl font-bold text-center">
                  📊 Detailní rozpočet akce
                </h4>
                <p className="text-center text-blue-100 mt-2">
                  {budget.note}
                </p>
              </div>

              {/* Kategorie s progress bary */}
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {budget.categories.map((category, idx) => {
                    const percentage = ((category.subtotal / budget.totalCost) * 100).toFixed(1);
                    const colors = [
                      'from-blue-500 to-blue-600',
                      'from-purple-500 to-purple-600',
                      'from-orange-500 to-orange-600',
                      'from-green-500 to-green-600'
                    ];
                    
                    return (
                      <Card key={idx} className="border-2 hover:shadow-lg transition-shadow">
                        <CardHeader className={`bg-gradient-to-r ${colors[idx]} text-white`}>
                          <CardTitle className="text-xl">{category.name}</CardTitle>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-2xl font-bold">
                              {category.subtotal.toLocaleString('cs-CZ')} Kč
                            </span>
                            <span className="text-lg bg-white/20 px-3 py-1 rounded-full">
                              {percentage}%
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {category.items.map((item, i) => (
                              <div key={i} className="flex justify-between items-start text-sm border-b border-gray-100 pb-2 last:border-0">
                                <span className="text-gray-700 flex-1">{item.name}</span>
                                <span className="font-semibold text-gray-900 ml-3">
                                  {item.cost.toLocaleString('cs-CZ')} Kč
                                </span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Vizuální rozdělení rozpočtu - Progress bars */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                  <h5 className="text-xl font-bold text-gray-900 mb-6 text-center">
                    Rozdělení celkového rozpočtu
                  </h5>
                  <div className="space-y-4">
                    {budget.categories.map((category, idx) => {
                      const percentage = ((category.subtotal / budget.totalCost) * 100);
                      const barColors = [
                        'bg-blue-500',
                        'bg-purple-500',
                        'bg-orange-500',
                        'bg-green-500'
                      ];
                      
                      return (
                        <div key={idx}>
                          <div className="flex justify-between mb-2">
                            <span className="font-semibold text-gray-700">{category.name}</span>
                            <span className="text-gray-900 font-bold">
                              {category.subtotal.toLocaleString('cs-CZ')} Kč ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                            <div 
                              className={`h-full ${barColors[idx]} transition-all duration-500 flex items-center justify-end pr-3`}
                              style={{ width: `${percentage}%` }}
                            >
                              <span className="text-white text-xs font-bold">
                                {percentage.toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Celkem */}
                  <div className="mt-6 pt-6 border-t-2 border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-gray-900">CELKEM:</span>
                      <span className="text-3xl font-bold text-blue-600">
                        {budget.totalCost.toLocaleString('cs-CZ')} Kč
                      </span>
                    </div>
                  </div>
                </div>

                {/* Shrnutí nákladů */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">Program & Umělci</p>
                    <p className="text-xl font-bold text-blue-600">47 500 Kč</p>
                    <p className="text-xs text-gray-500">23%</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">Technika</p>
                    <p className="text-xl font-bold text-purple-600">40 505 Kč</p>
                    <p className="text-xs text-gray-500">20%</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">Propagace</p>
                    <p className="text-xl font-bold text-orange-600">51 677 Kč</p>
                    <p className="text-xs text-gray-500">25%</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 mb-1">Organizační</p>
                    <p className="text-xl font-bold text-green-600">65 416 Kč</p>
                    <p className="text-xs text-gray-500">32%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prémiová varianta - Tvář festivalu */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
              ⭐ PRÉMIOVÁ MOŽNOST
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              {premiumOption.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {premiumOption.subtitle}
            </p>
          </div>

          {/* Výhody */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
            {premiumOption.benefits?.map((benefit, idx) => (
              <Card key={idx} className="border-2 border-yellow-400 bg-white shadow-lg">
                <CardContent className="pt-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                  <p className="text-xl font-bold text-gray-900">{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Celebrity šéfkuchaři */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {premiumOption.celebrities?.map((celebrity, idx) => {
              const IconComponent = iconComponents[celebrity.icon] || Star;
              return (
                <Card key={idx} className="bg-white border-2 border-purple-300 hover:border-purple-500 transition-all hover:shadow-xl">
                  <CardHeader className="bg-gradient-to-br from-purple-600 to-pink-600 text-white">
                    <div className="flex items-center justify-center mb-2">
                      <IconComponent className="h-8 w-8" />
                    </div>
                    <CardTitle className="text-center text-lg">{celebrity.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-600 text-center">{celebrity.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Cena prémiové varianty */}
          <div className="max-w-2xl mx-auto">
            <Card className="border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-2xl">
              <CardContent className="pt-8 pb-8 text-center">
                <p className="text-sm text-gray-600 mb-2">Dodatečný náklad</p>
                <p className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-4">
                  {premiumOption.additionalCost}
                </p>
                <p className="text-gray-700 max-w-md mx-auto mb-3">
                  Investice do prémiové varianty přináší výrazně vyšší mediální dosah a prestiž akce
                </p>
                {premiumOption.note && (
                  <p className="text-sm text-gray-600 italic mt-4">
                    {premiumOption.note}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Kontakt */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-purple-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Pojďme to společně uskutečnit
          </h2>
          <p className="text-xl text-blue-100 mb-12">
            Rádi vám poskytneme další informace a zodpovíme vaše dotazy
          </p>

          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardContent className="pt-8">
              <div className="space-y-6">
                <div>
                  <p className="text-2xl font-bold mb-2">{contactInfo.name}</p>
                  <p className="text-blue-100">{contactInfo.company}</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a 
                    href={`tel:${contactInfo.phone}`}
                    className="flex items-center gap-3 bg-white/20 hover:bg-white/30 px-6 py-3 rounded-full transition-all"
                  >
                    <Phone className="h-5 w-5" />
                    <span className="font-semibold">{contactInfo.phone}</span>
                  </a>
                  
                  <a 
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-center gap-3 bg-white/20 hover:bg-white/30 px-6 py-3 rounded-full transition-all"
                  >
                    <Mail className="h-5 w-5" />
                    <span className="font-semibold">{contactInfo.email}</span>
                  </a>
                </div>

                <div className="pt-6">
                  <Button 
                    onClick={() => window.location.href = `mailto:${contactInfo.email}?subject=Zájem o partnerství - Gastrofestival Luhačovice 2027`}
                    className="bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold text-lg px-12 py-6 rounded-full shadow-2xl hover:shadow-yellow-500/50 transition-all transform hover:scale-105"
                  >
                    NAPSAT EMAIL
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p>&copy; 2027 {contactInfo.company}. Gastrofestival Cesta Kolem Světa - Luhačovice</p>
        </div>
      </footer>
    </div>
  );
};

export default LuhacoviceLanding;
